import { MailCheck, MailOpen } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import { ROUTES, routeTo } from "@/app/routes";
import { useSession } from "@/core/auth/session";
import { ROLE_LABEL } from "@/core/brand/brand-context";
import { useMyBrands } from "@/core/brand/my-brands";
import { formatDate } from "@/core/format/format";
import { useDocumentTitle } from "@/core/hooks/use-document-title";
import { SignOutButton } from "@/layouts/SignOutButton";
import { SimpleLayout } from "@/layouts/SimpleLayout";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { ConfirmDialog } from "@/ui/confirm-dialog";
import { Skeleton } from "@/ui/spinner";
import { EmptyState, ErrorState, PageHeader } from "@/ui/states";

import {
  useAcceptInvitation,
  useDeclineInvitation,
  useInvitations,
  type Invitation,
} from "./invitations.queries";

/** Invitations need a verified email: say where the link went and how to get a new one. */
function VerifyEmail({ email }: { email: string | null }) {
  return (
    <Card className="flex flex-col items-start gap-4">
      <MailCheck className="size-9 text-info" aria-hidden="true" />
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-bold">Verify your email first</h2>
        <p className="text-sm text-ink-2">
          We sent a link to <strong>{email ?? "your email"}</strong>. Open it, then sign in again here to see
          your invitations.
        </p>
        <p className="text-sm text-muted">No email? Check spam. Signing in again sends a new link.</p>
      </div>
      <SignOutButton>Sign out to sign in again</SignOutButton>
    </Card>
  );
}

function InvitationItem({
  invitation,
  onDecline,
}: {
  invitation: Invitation;
  onDecline: (invitation: Invitation) => void;
}) {
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const expired = invitation.status === "EXPIRED";
  const onAccept = () =>
    accept.mutate(invitation.id, {
      onSuccess: (joined) => {
        toast.success(`You're in the ${joined.brandName} team.`);
        void navigate(routeTo.brandHome(joined.brandId));
      },
      onError: (error) => toast.error(error.message),
    });

  return (
    <li className="flex flex-col gap-3 border-t border-border-subtle px-4 py-4 first:border-t-0 sm:flex-row sm:items-center sm:px-5">
      <div className="flex min-w-0 grow flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-bold">{invitation.brandName}</span>
          <Badge tone="info">{ROLE_LABEL[invitation.role]}</Badge>
        </span>
        <span className="text-[13px] text-muted">
          {invitation.invitedByName
            ? `Invited by ${invitation.invitedByName}`
            : "Invited by the Brocoly team"}
          {" · "}
          {expired ? "Expired. Ask for a new invitation." : `Open until ${formatDate(invitation.expiresAt)}`}
        </span>
      </div>
      {!expired && (
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" onClick={() => onDecline(invitation)} disabled={accept.isPending}>
            Decline
          </Button>
          <Button onClick={onAccept} loading={accept.isPending}>
            Join
          </Button>
        </div>
      )}
    </li>
  );
}

/** Joining a brand team: open invitations for this email, or what to do when there are none. */
export function InvitationsPage() {
  useDocumentTitle("Invitations");
  const { me } = useSession();
  const verified = Boolean(me?.email && me.emailVerified);
  const invitations = useInvitations(verified);
  const brands = useMyBrands();
  const decline = useDeclineInvitation();
  const [declining, setDeclining] = useState<Invitation | null>(null);
  const hasBrands = (brands.data?.length ?? 0) > 0;

  return (
    <SimpleLayout>
      <PageHeader
        title="Join a brand team"
        subtitle={me?.email ? `Invitations for ${me.email}` : undefined}
      />
      {!verified ? (
        <VerifyEmail email={me?.email ?? null} />
      ) : invitations.isError ? (
        <ErrorState error={invitations.error} onRetry={() => void invitations.refetch()} />
      ) : invitations.isPending ? (
        <Skeleton className="h-24 rounded-2xl" />
      ) : invitations.data.length === 0 ? (
        <EmptyState
          icon={MailOpen}
          title="No invitations yet"
          body={
            <>
              Ask your brand's Owner to invite <strong>{me?.email}</strong>, then come back here. Starting a
              new kitchen on Brocoly? Contact the Brocoly team.
            </>
          }
          action={
            <Button
              variant="secondary"
              onClick={() => void invitations.refetch()}
              loading={invitations.isFetching}
            >
              Check again
            </Button>
          }
        />
      ) : (
        <Card className="p-0">
          <ul aria-label="Invitations">
            {invitations.data.map((invitation) => (
              <InvitationItem key={invitation.id} invitation={invitation} onDecline={setDeclining} />
            ))}
          </ul>
        </Card>
      )}
      {hasBrands && (
        <Link to={ROUTES.start} className="self-start text-sm font-bold">
          Back to your brands
        </Link>
      )}

      {declining && (
        <ConfirmDialog
          title="Decline this invitation?"
          subject={`${declining.brandName} · ${ROLE_LABEL[declining.role]}`}
          effect="You won't join this team. To join later, ask them to invite you again."
          confirmLabel="Decline"
          busy={decline.isPending}
          error={decline.error}
          onConfirm={() =>
            decline.mutate(declining.id, {
              onSuccess: () => {
                setDeclining(null);
                decline.reset();
              },
            })
          }
          onClose={() => {
            setDeclining(null);
            decline.reset();
          }}
        />
      )}
    </SimpleLayout>
  );
}
